import { isCommonWord, useControlled } from "@commonlibs";
import { useState } from "react";
import { PasswordTextFieldProps } from "../PasswordTextField.types";
import getPasswordProgress from "../utils/getPasswordProgress";
import isEnoughPasswordProgress from "../utils/isPasswordEnoughtStrong";
import { useTranslate } from "ui/locale";
import { useSearchParams } from "react-router-dom";
import { useGetUserInfo } from "features/users/hooks/useGetUserInfo";
import { UserDto } from "common/models";
import useIsLoggedIn from "ui/auth/hooks/useIsLoggedIn";

export type PasswordValidation = {
    message: string;
    isError: boolean;
};

export type UsePasswordTextFieldResult = {
    value: PasswordTextFieldProps["value"];
    setValue: PasswordTextFieldProps["onChange"];
    progress: number;
    showPassword: boolean;
    handleShowPassword: () => void;
    validatePassword: (value: string, validate: boolean) => PasswordValidation;
};

export default function usePasswordTextField({
    defaultValue = "",
    value: valueProp,
    onChange,
}: PasswordTextFieldProps): UsePasswordTextFieldResult {
    const [value, setValue] = useControlled(defaultValue, valueProp, onChange);
    const [progress, setProgress] = useState(0);
    const [showPassword, setShowPassword] = useState(false);
    const translate = useTranslate();
    const [searchParams] = useSearchParams();
    const userParams: Partial<UserDto> = {
        email: searchParams.get("email") || "",
        phone: searchParams.get("phone") || "",
        fullName: searchParams.get("fullname") || "",
    };

    const isLoggedIn = useIsLoggedIn();

    const { data: userData } = useGetUserInfo({ enabled: isLoggedIn });

    //Handlers
    function handleValueChange(newValue: string) {
        setValue(newValue);
        setProgress(getPasswordProgress(newValue));
    }

    function handleShowPassword() {
        setShowPassword((prev) => !prev);
    }

    const genUserWords = (user: Partial<UserDto>): string[] => {
        return Object.values(user).filter((value) => typeof value === "string") as string[];
    };

    const NO_ERROR_VALIDATION = {
        message: translate("invalid_weak_password", { ns: "error" }),
        isError: false,
    };

    const validatePassword = (value: string, validate: boolean): PasswordValidation => {
        if (!validate) {
            return NO_ERROR_VALIDATION;
        }

        const weakValidation = value !== "" && !isEnoughPasswordProgress(progress);

        if (weakValidation) {
            return {
                message: translate("invalid_weak_password", { ns: "error" }),
                isError: true,
            };
        }

        const userWords = genUserWords(userData ?? userParams);

        const commonWordsError = isCommonWord(value, userWords);

        if (commonWordsError) {
            return {
                message: translate("is_common_word", { ns: "error" }),
                isError: true,
            };
        }

        return NO_ERROR_VALIDATION;
    };

    return {
        value,
        setValue: handleValueChange,
        progress,
        showPassword,
        handleShowPassword,
        validatePassword,
    };
}
